import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SEO from '../components/SEO';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // Basic client-side validation
    if (!formData.name || !formData.email || !formData.message) {
      setError('Please fill in all required fields.');
      setIsSubmitting(false);
      return;
    }

    try {
      const response = await fetch('api/send_email.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const result = await response.json();

      if (response.ok) {
      setSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });
      } else {
        setError(result.message || 'There was an error submitting your request. Please try again.');
      }
    } catch (err) {
      console.error('Submission error:', err);
      setError('There was an error submitting your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page pt-24 pb-16 relative">
      <SEO 
        title="Contact Us | Drone Services in Lehigh Valley, PA"
        description="Contact Cosmik Drones for professional drone photography and videography services in Lehigh Valley. Get quotes for aerial projects across Pennsylvania and the East Coast."
        canonicalUrl="https://cosmikdrones.com/contact"
      />
      
      {/* Background Image */}
      <div className="absolute inset-0 z-0">
        <img 
          src="/images/Fall - Lone Lane - 9.jpg" 
          alt="Aerial view of Lehigh Valley" 
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-darkBg/80 to-darkBg/95"></div>
      </div>
      
      <div className="section-container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-lightText mb-6">Contact Us</h1>
          <p className="text-xl text-lightText/80 max-w-4xl mx-auto">
            Ready to elevate your project with stunning aerial cinematography? Reach out to us for a consultation or quote.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6 }}
            className="glass-card p-8 rounded-lg backdrop-blur-sm bg-darkBg/80"
          >
            <h2 className="text-2xl font-bold text-lightText mb-6">Send Us a Message</h2>
            
            {!submitted ? (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-lightText/70 mb-2">Your Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-darkCard text-lightText rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="email" className="block text-lightText/70 mb-2">Email Address</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-darkCard text-lightText rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                      required
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="block text-lightText/70 mb-2">Phone Number</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-darkCard text-lightText rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                  </div>
                </div>
                
                <div>
                  <label htmlFor="service" className="block text-lightText/70 mb-2">Service Interested In</label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="w-full px-4 py-3 bg-darkCard text-lightText rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select a Service</option>
                    <option value="Aerial Photography">Aerial Photography</option>
                    <option value="Cinematic Videography">Cinematic Videography</option>
                    <option value="FPV Drone Filming">FPV Drone Filming</option>
                    <option value="Real Estate">Real Estate & Property</option>
                    <option value="Construction">Construction & Mapping</option>
                    <option value="Events">Events & Sports</option>
                    <option value="Other">Other (Specify in Message)</option>
                  </select>
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-lightText/70 mb-2">Your Message</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="5"
                    className="w-full px-4 py-3 bg-darkCard text-lightText rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  ></textarea>
                </div>
                
                {error && (
                  <div className="text-red-500 bg-red-500/10 p-3 rounded-md">
                    {error}
                  </div>
                )}
                
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="bg-primary hover:bg-primary/90 text-darkBg font-bold py-3 px-8 rounded-md text-lg transition-colors inline-block w-full"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            ) : (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-6">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-2xl font-bold text-lightText mb-4">Thank You!</h3>
                <p className="text-lightText/80 mb-6">
                  Your message has been received. We'll get back to you within 24-48 business hours.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="bg-primary/20 hover:bg-primary/30 text-primary font-medium py-2 px-6 rounded-md text-lg transition-colors inline-block"
                >
                  Send Another Message
                </button>
              </div>
            )}
          </motion.div>
          
          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="glass-card p-8 rounded-lg mb-8 backdrop-blur-sm bg-darkBg/80">
              <h2 className="text-2xl font-bold text-lightText mb-6">Contact Information</h2>
              <div className="space-y-4">
                                <div className="flex items-start">                  <div className="bg-primary/20 p-3 rounded-full mr-4">                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />                    </svg>                  </div>                  <div>                    <h3 className="text-lightText font-semibold mb-1">Phone</h3>                    <p className="text-lightText/80">(610) 509-0867</p>                  </div>                </div>
                                <div className="flex items-start">                  <div className="bg-primary/20 p-3 rounded-full mr-4">                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />                    </svg>                  </div>                  <div>                    <h3 className="text-lightText font-semibold mb-1">Email</h3>                    <p className="text-lightText/80">Josh@Cosmikdrones.com</p>                  </div>                </div>
                <div className="flex items-start">
                  <div className="bg-primary/20 p-3 rounded-full mr-4">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <h3 className="text-lightText font-semibold mb-1">Location</h3>
                    <p className="text-lightText/80">Bethlehem, Pennsylvania</p>
                    <p className="text-lightText/80">Serving the entire Lehigh Valley and beyond</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="glass-card p-8 rounded-lg">
              <h2 className="text-2xl font-bold text-lightText mb-6">Business Hours</h2>
              <ul className="space-y-3">
                <li className="flex justify-between">
                  <span className="text-lightText/70">Monday - Sunday</span>
                  <span className="text-lightText font-medium">Open 24/7</span>
                </li>
                <li className="flex justify-between">
                  <span className="text-lightText/70">Contact Response Time</span>
                  <span className="text-lightText font-medium">Within 24 Hours</span>
                </li>
              </ul>
              <div className="mt-6 pt-6 border-t border-gray-700">
                <p className="text-lightText/70">
                  <span className="text-primary font-semibold">Note:</span> Filming hours are flexible and can be arranged according to your project needs, including weekends and evenings.
                </p>
              </div>
            </div>
            
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-lightText mb-6">Connect With Us</h2>
              <div className="flex space-x-4">
                <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="bg-gray-800 hover:bg-gray-700 w-12 h-12 rounded-full flex items-center justify-center transition-colors">
                  <svg className="h-6 w-6 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M22.675 0h-21.35c-.732 0-1.325.593-1.325 1.325v21.351c0 .731.593 1.324 1.325 1.324h11.495v-9.294h-3.128v-3.622h3.128v-2.671c0-3.1 1.893-4.788 4.659-4.788 1.325 0 2.463.099 2.795.143v3.24l-1.918.001c-1.504 0-1.795.715-1.795 1.763v2.313h3.587l-.467 3.622h-3.12v9.293h6.116c.73 0 1.323-.593 1.323-1.325v-21.35c0-.732-.593-1.325-1.325-1.325z" />
                  </svg>
                </a>
                <a href="https://www.instagram.com/josh.kolonia/" target="_blank" rel="noopener noreferrer" className="bg-gray-800 hover:bg-gray-700 w-12 h-12 rounded-full flex items-center justify-center transition-colors">
                  <svg className="h-6 w-6 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                  </svg>
                </a>
                <a href="https://twitter.com" target="_blank" rel="noopener noreferrer" className="bg-gray-800 hover:bg-gray-700 w-12 h-12 rounded-full flex items-center justify-center transition-colors">
                  <svg className="h-6 w-6 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                  </svg>
                </a>
                <a href="https://youtube.com" target="_blank" rel="noopener noreferrer" className="bg-gray-800 hover:bg-gray-700 w-12 h-12 rounded-full flex items-center justify-center transition-colors">
                  <svg className="h-6 w-6 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M19.615 3.184c-3.604-.246-11.631-.245-15.23 0-3.897.266-4.356 2.62-4.385 8.816.029 6.185.484 8.549 4.385 8.816 3.6.245 11.626.246 15.23 0 3.897-.266 4.356-2.62 4.385-8.816-.029-6.185-.484-8.549-4.385-8.816zm-10.615 12.816v-8l8 3.993-8 4.007z" />
                  </svg>
                </a>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Lehigh Valley Service Area Map */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mt-16"
        >
          <div className="glass-card p-0 rounded-lg overflow-hidden">
            <div className="relative h-[500px]">
              {/* Google Maps Embed */}
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d193175.30899084397!2d-75.59335556896967!3d40.65863379176778!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c43e7e514daac1%3A0xbf2946962c46811c!2sLehigh%20Valley%2C%20PA!5e0!3m2!1sen!2sus!4v1699373652896!5m2!1sen!2sus" 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Lehigh Valley Service Area Map"
                className="absolute inset-0"
              ></iframe>
              
              {/* Map Overlay */}
              <div className="absolute inset-0 pointer-events-none">
                {/* Primary Service Area Circle - Centered on Bethlehem */}
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full border-4 border-primary/40 border-dashed animate-pulse-slow"></div>
              </div>
              
              {/* Map Info Box */}
              <div className="absolute bottom-0 left-0 right-0 bg-darkBg/90 p-6 backdrop-blur-sm">
                <h3 className="text-2xl font-bold text-lightText mb-2">Service Area</h3>
                <p className="text-lightText/80 max-w-2xl">
                  Cosmik Drones is based in Bethlehem and proudly serves the entire Lehigh Valley including Allentown, Bethlehem, Easton, and surrounding areas. We're also available for projects throughout Pennsylvania, New Jersey, and beyond.
                </p>
                <div className="flex flex-wrap gap-4 mt-4">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-nebula rounded-full mr-2"></div>
                    <span className="text-lightText text-sm">Home Base (Bethlehem)</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-primary rounded-full mr-2"></div>
                    <span className="text-lightText text-sm">Primary Service Area (Lehigh Valley)</span>
                  </div>
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-cosmic/80 rounded-full mr-2"></div>
                    <span className="text-lightText text-sm">Extended Service Area (Eastern PA)</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="mt-24 text-center"
        >
          <h2 className="text-3xl font-bold text-lightText mb-6">Need a Quick Response?</h2>
          <p className="text-xl text-lightText/80 max-w-3xl mx-auto mb-8">
            For urgent inquiries, you can reach us directly by phone at (610) 509-0867 during business hours.
          </p>
          <a 
            href="tel:6105090867" 
            className="bg-primary hover:bg-primary/90 text-darkBg font-bold py-3 px-8 rounded-md text-lg transition-colors inline-flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            Call Us Now
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact; 